import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Button,
    TextField,
    Box,
    Paper,
    Alert,
    Stack,
} from "@mui/material";
import { useFetch } from "./FetchProvider";
import { FormattedMessage, useIntl } from "react-intl";

const baseApiUrl = "/api/user/totp";

type GenerateSecretStageProps = {
    onSuccess: () => void;
};

const GenerateSecretStage = ({ onSuccess }: GenerateSecretStageProps) => {
    const [secret, setSecret] = useState<string | null>(null);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState<string>("");
    const [verificationError, setVerificationError] = useState<string | null>(null);
    const fetch = useFetch();
    const intl = useIntl();

    useEffect(() => {
        const generate = async () => {
            const response = await fetch(baseApiUrl + "/generate-secret");

            // 500 and above is handled by FetchProvider
            if (response.status !== 200 && response.status < 500) {
                alert("Unexpected error");
                return;
            }

            const data = await response.json();
            setSecret(data.secret);
            setQrCodeDataUrl(await QRCode.toDataURL(data["provisioning_uri"]));
        };

        generate();
    }, [fetch, setSecret, setQrCodeDataUrl]);

    const QRImage = () => {
        if (!secret) return null;

        return (
            <Box
                component="img"
                src={qrCodeDataUrl}
                sx={{
                    border: "1px solid #ccc",
                    borderRadius: 1,
                    mb: 0.5,
                }}
                alt="QR Code"
            />
        );
    };

    const submit = async () => {
        const response = await fetch(baseApiUrl + "/enable", {
            method: "post",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ code: verificationCode }),
        });

        // 500 and above is handled by FetchProvider
        if (response.status !== 200 && response.status < 500) {
            alert("Unexpected error");
            return;
        }

        const data = await response.json();
        setVerificationError(data.error);
        if (!data.error) onSuccess();
    };

    const VerificationErrorText = () => {
        if (!verificationError) return null;

        const name = `account.totp.generate_stage.code_verification.error.${verificationError}`;

        return (
            <Typography color="error" sx={{ p: "0.3em 0.6em" }}>
                <FormattedMessage id={name} />
            </Typography>
        );
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
                <FormattedMessage id="account.totp.generate_stage.title" />
            </Typography>
            <Box>
                <Typography sx={{ my: 0.5 }}>
                    <FormattedMessage id="account.totp.generate_stage.image_label" />
                </Typography>
                <Box>
                    <QRImage />
                </Box>
                <Typography sx={{ my: 0.5 }}>
                    <FormattedMessage id="account.totp.generate_stage.code_label" />
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, fontFamily: "monospace" }}>
                    {secret}
                </Paper>
                <Typography sx={{ my: 0.5 }}>
                    <FormattedMessage id="account.totp.generate_stage.code_verification.label" />
                </Typography>
                <TextField
                    value={verificationCode}
                    onChange={(ev) => setVerificationCode(ev.target.value)}
                    fullWidth
                    error={!!verificationError}
                    inputProps={{ maxLength: 6 }}
                    placeholder={intl.formatMessage({
                        id: "account.totp.generate_stage.code_verification.placeholder",
                    })}
                    sx={{ mb: 1 }}
                />
                <VerificationErrorText />
            </Box>
            <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button disabled={verificationCode.length !== 6} onClick={submit}>
                    <FormattedMessage id="account.totp.generate_stage.submit" />
                </Button>
            </Box>
        </Box>
    );
};

type DisabledContentProps = {
    onSuccess: () => void;
    initialStage: number;
};

const DisabledContent = ({ onSuccess, initialStage }: DisabledContentProps) => {
    const [stage, setStage] = useState<number>(initialStage);

    const InfoStage = () => (
        <Stack spacing={2}>
            <Alert severity="warning">
                <FormattedMessage id="account.totp.disabled_text" />
            </Alert>
            <Button onClick={() => setStage(1)}>
                <FormattedMessage id="account.totp.enable_button" />
            </Button>
        </Stack>
    );

    switch (stage) {
        case 0:
            return <InfoStage />;
        case 1:
            return <GenerateSecretStage onSuccess={onSuccess} />;
        default:
            return null;
    }
};

type EnabledContentProps = {
    onSuccess: () => void;
    onReconfigure: () => void;
};

const EnabledContent = ({ onSuccess, onReconfigure }: EnabledContentProps) => {
    const fetch = useFetch();

    const disableTOTP = async () => {
        const response = await fetch(baseApiUrl + "/disable");

        // 500 and above is handled by FetchProvider
        if (response.status !== 200 && response.status < 500) {
            alert("Unexpected error");
            return;
        }

        onSuccess();
    };

    return (
        <Stack spacing={2}>
            <Alert severity="success">
                <FormattedMessage id="account.totp.enabled_text" />
            </Alert>
            <Stack direction="row">
                <Button color="error" onClick={disableTOTP}>
                    <FormattedMessage id="account.totp.disable_button" />
                </Button>
                <Button color="error" onClick={onReconfigure}>
                    <FormattedMessage id="account.totp.reconfigure_button" />
                </Button>
            </Stack>
        </Stack>
    );
};

type DialogProps = {
    show: boolean;
    onHide: () => void;
};

const OTPDialog = ({ show, onHide }: DialogProps) => {
    const [enabled, setEnabled] = useState(false);
    const [initialDisabledStage, setInitialDisabledStage] = useState(0);
    const fetch = useFetch();

    const fetchData = async () => {
        const response = await fetch(baseApiUrl + "/info");
        if (response.status !== 200) {
            alert("Unexpected error");
            return;
        }

        const data = await response.json();
        setEnabled(data.enabled);
    };

    const Header = () => (
        <DialogTitle>
            <FormattedMessage id="account.totp.settings" />
        </DialogTitle>
    );

    const onEnabled = () => {
        setEnabled(true);
    };

    const onDisabled = () => {
        setEnabled(false);
    };

    const onReconfigure = () => {
        setEnabled(false);
        setInitialDisabledStage(1);
    };

    const Body = () => (
        <DialogContent>
            {enabled ? (
                <EnabledContent onSuccess={onDisabled} onReconfigure={onReconfigure} />
            ) : (
                <DisabledContent onSuccess={onEnabled} initialStage={initialDisabledStage} />
            )}
        </DialogContent>
    );

    // Use onEnter and onExited with the Dialog component properly
    useEffect(() => {
        if (show) {
            fetchData();
        }
    }, [show]);

    // Reset stage when dialog closes
    useEffect(() => {
        if (!show) {
            setInitialDisabledStage(0);
        }
    }, [show]);

    return (
        <Dialog open={show} onClose={onHide} maxWidth="sm" fullWidth>
            <Header />
            <Body />
        </Dialog>
    );
};

const AccountOTPSettingsDialog = () => {
    const [showDialog, setShowDialog] = useState<boolean>(false);

    console.debug("Showing dialog", showDialog);

    return (
        <Box>
            <Button variant="contained" size="small" onClick={() => setShowDialog(!showDialog)}>
                <FormattedMessage id="account.totp.settings" />
            </Button>
            <OTPDialog show={showDialog} onHide={() => setShowDialog(false)} />
        </Box>
    );
};

export default AccountOTPSettingsDialog;
